import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  styled,
  Switch,
  SwitchProps,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useEnv } from 'src/context/EnvProvider';
import useLoadDataList from 'src/hook/useLoadDataList';
import { useTexture } from 'src/hook/useTexture';
import { StateNames, States } from 'src/Screens/Configurator';
import { getGranits, Granit } from 'src/services/granits';
import { Piece } from 'src/services/pieces';
import { getPiece, getPieceSize } from 'src/utils/configurator.utils';
import * as THREE from 'three';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RemoveIcon from '@mui/icons-material/Remove';
import { GranitCategories, granitCategoriesLabels } from 'src/constants/category';
import { configureHDTexture } from 'src/utils/textureLoader';
import {
  getGlobalIndex,
  getIndexType,
  getLocalIndex,
  isTwoSteleEqual,
  pieceHasBeenModified,
  getDimensionsWithGeometry,
} from 'src/utils/3d.utils';
import { AdvancedMode } from '../../Fragments/AdvancedMode';
import {
  getAssemblyStockWithoutGranit,
  getStocksByPieceId,
  getStocksByStele,
} from 'src/services/stock';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';

type TexturesProps = {
  pieceIndex: number;
  activePieces: any[];
  setActivePieces: (pieces: Piece[]) => void;
  customSteles: any[];
  setCustomSteles: (customSteles: any[]) => void;
  customPlatings: any[];
  setCustomPlatings: (customPlatings: any[]) => void;
  setPieceIndex: (pieceIndex: number) => void;
  getValue: <T extends StateNames>(stateName: T) => States[T];
  setValue: <T extends StateNames>(stateName: T, value: States[T]) => void;
  pieces: any[];
  customSoles: any[];
  setCustomSoles: (customSoles: any[]) => void;
  history: {
    [k: string]: {
      [k: string]: Float32Array;
    };
  };
};

type SortType = 'ascending_price' | 'descending_price' | 'ascending_name' | 'descending_name';
const ORIGINS = ['FRENCH', 'CHINA', 'INDIA', 'OTHER', 'IMPORT'];
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export const Textures = ({
  activePieces,
  setActivePieces,
  pieceIndex,
  customSteles,
  setCustomSteles,
  customPlatings,
  setCustomPlatings,
  setPieceIndex,
  getValue,
  pieces,
  setValue,
  customSoles,
  setCustomSoles,
  history,
}: TexturesProps) => {
  const { buckets } = useEnv();

  const { granits } = useTexture();
  const [selectedDominant, setSelectedDominant] = useState<string>();
  const [hasSelectedAllPieces, setHasSelectedAllPieces] = useState<boolean>(false);

  const [filter, setFilter] = useState<string>('');
  const [error, setError] = useState<string>();
  const [sort, setSort] = useState<SortType>('ascending_name');
  const [categories, setCategories] = useState<string[]>([]);
  const [origin, setOrigin] = React.useState<string[]>([]);

  const [seeAssemblyStock, setSeeAssemblyStock] = useState<boolean>(false);

  const type = getIndexType(pieceIndex, activePieces, customSteles, customPlatings, customSoles);
  const piece = getPiece(pieces, customSteles, customPlatings, customSoles, pieceIndex);

  // const hasBeenModified = pieceHasBeenModified(piece, history);

  if (selectedDominant && activePieces[pieceIndex]?.granit?.id !== selectedDominant) {
    setSelectedDominant(activePieces[pieceIndex]?.granit?.id);
  }

  const { data: stockData, onRefresh: refreshStock } = useLoadDataList(
    () =>
      getStocksByPieceId(
        piece.id,
        getDimensionsWithGeometry(piece?.geometry?.attributes?.position?.array),
      ),
    [piece.id, piece],
    type === 'activePieces',
  );

  const { data: stockStelesData, onRefresh: refreshStockSteles } = useLoadDataList(
    () =>
      getStocksByStele({
        baseIndex: Number(piece.baseIndex),
        boxDownWidth: Number(piece.boxDownWidth),
        boxHeight: Number(piece.boxHeight),
        boxLeftHeight: Number(piece.boxLeftHeight),
        boxRightHeight: Number(piece.boxRightHeight),
        boxUpWidth: Number(piece.boxUpWidth),
        headIndex: Number(piece.headIndex),
        depth: Number(piece.depth),
      }),
    [piece.id],
    type === 'customStele',
  );

  const { data: assemblyStockData, onRefresh: refreshAssemblyStock } = useLoadDataList(
    () =>
      getAssemblyStockWithoutGranit({
        stockPieces: pieces.map((el) => {
          const activePiece = activePieces.find((p) => p.id === el.id);

          const { x, y, z } = getDimensionsWithGeometry(el.geometry.attributes.position.array);

          const weight = Math.round((activePiece?.granit?.weight ?? 0) * ((x * y * z) / 100));

          return {
            pieceId: el.id ?? -1,
            granitId: activePiece.granit?.id ?? '',
            newGeometry: [...el.geometry.attributes.position.array],
            x,
            y,
            z,
            weight,
          };
        }),
      }),
    [pieces],
    type === 'activePieces',
  );

  useEffect(() => {
    if (type === 'activePieces') {
      refreshStock();
    }
  }, [piece.id, type]);

  const checkMonumentSizeWithGranit = (pieceGeometry: Float32Array, granitMaxLength: string) => {
    const size = {
      x: getPieceSize('x', pieceGeometry),
      y: getPieceSize('y', pieceGeometry),
      z: getPieceSize('z', pieceGeometry),
    };

    if (
      Number.parseFloat(size.x) > Number.parseFloat(granitMaxLength) ||
      Number.parseFloat(size.y) > Number.parseFloat(granitMaxLength) ||
      Number.parseFloat(size.z) > Number.parseFloat(granitMaxLength)
    ) {
      setError(
        `Attention, une de vos pièces dépasse la taille maximale de ce granit (${Number.parseFloat(
          granitMaxLength,
        )}). Elle sera coupée.`,
      );
    }
  };

  const changeTexture = (granit: Granit, indexs: number[]) => {
    const loader = new THREE.TextureLoader();
    const loadedTexture = loader.load(
      `${buckets?.BUCKET_3D_PIECES || ''}/${granit.image}?t=${Date.now()}`,
      // Callback quand la texture est chargée - applique les paramètres HD
      (texture) => {
        configureHDTexture(texture);
      },
    );

    let newActivePieces = [...activePieces];
    let newCustomStele = [...customSteles];
    let newCustomPlating = [...customPlatings];
    let newCustomSoles = [...customSoles];
    const hasAdvancedMode = getValue('hasAdvancedMode');

    for (const index of indexs) {
      let typeOfPiece = 'activePieces';

      typeOfPiece = getIndexType(index, activePieces, customSteles, customPlatings, customSoles);

      if (typeOfPiece === 'activePieces') {
        loadedTexture.wrapS = THREE.RepeatWrapping;
        loadedTexture.wrapT = THREE.MirroredRepeatWrapping;
        loadedTexture.repeat.x = 1;

        newActivePieces = newActivePieces.map((el, elIndex) => {
          if (
            elIndex === index ||
            (activePieces[index].underAssembly &&
              activePieces[index].underAssembly.uuid ===
                activePieces[elIndex].underAssembly?.uuid &&
              !hasAdvancedMode)
          ) {
            checkMonumentSizeWithGranit(
              pieces[index].geometry.attributes.position.array,
              String(granit.maxLength),
            );
            return {
              ...el,
              granit,
              texture: loadedTexture,
            };
          }
          return el;
        });
      } else if (typeOfPiece === 'customStele') {
        loadedTexture.wrapS = THREE.RepeatWrapping;
        loadedTexture.wrapT = THREE.MirroredRepeatWrapping;
        loadedTexture.offset.x = 0.5;
        loadedTexture.repeat.x = 0.5;
        loadedTexture.repeat.y = 0.5;
        newCustomStele = newCustomStele.map((el, elIndex) => {
          if (index === activePieces.length + elIndex) {
            const extrudeGeometry = new THREE.ExtrudeGeometry(el.shape, el.extrudeSettings);
            checkMonumentSizeWithGranit(
              // @ts-ignore
              extrudeGeometry.attributes.position.array,
              String(granit.maxLength),
            );
            return {
              ...el,
              granit,
              texture: loadedTexture,
            };
          }
          return el;
        });
      } else if (typeOfPiece === 'customPlating') {
        loadedTexture.wrapS = THREE.RepeatWrapping;
        loadedTexture.wrapT = THREE.MirroredRepeatWrapping;
        loadedTexture.offset.x = 0.5;
        loadedTexture.repeat.x = 0.5;
        loadedTexture.repeat.y = 0.5;
        newCustomPlating = newCustomPlating.map((el, elIndex) => {
          const deltaIndex = (activePieces.length ?? 0) + (customSteles.length ?? 0);
          if (index >= deltaIndex && index < deltaIndex + (customPlatings.length ?? 0)) {
            if (hasAdvancedMode && index - deltaIndex !== elIndex) return el;
            const extrudeGeometry = new THREE.ExtrudeGeometry(el.shape, {
              curveSegments: 12,
              steps: 2,
              depth: el.shapeDetails.depth,
              bevelEnabled: false,
            });
            checkMonumentSizeWithGranit(
              // @ts-ignore
              extrudeGeometry.attributes.position.array,
              String(granit.maxLength),
            );

            return {
              ...el,
              granit,
              texture: loadedTexture,
            };
          }
          return el;
        });
      } else if (typeOfPiece === 'customSole') {
        loadedTexture.wrapS = THREE.RepeatWrapping;
        loadedTexture.wrapT = THREE.MirroredRepeatWrapping;
        loadedTexture.offset.x = 0.5;
        loadedTexture.repeat.x = 0.5;
        loadedTexture.repeat.y = 0.5;
        newCustomSoles = newCustomSoles.map((el, elIndex) => {
          const deltaIndex =
            (activePieces.length ?? 0) + (customSteles.length ?? 0) + (customPlatings.length ?? 0);
          if (index >= deltaIndex && index < deltaIndex + (customSoles.length ?? 0)) {
            if (hasAdvancedMode && index - deltaIndex !== elIndex) return el;
            const extrudeGeometry = new THREE.ExtrudeGeometry(el.shape, {
              curveSegments: 12,
              steps: 2,
              depth: el.shapeDetails.depth,
              bevelEnabled: false,
            });
            checkMonumentSizeWithGranit(
              // @ts-ignore
              extrudeGeometry.attributes.position.array,
              String(granit.maxLength),
            );

            return {
              ...el,
              granit,
              texture: loadedTexture,
            };
          }
          return el;
        });
      }
    }

    setActivePieces(newActivePieces);
    setCustomPlatings(newCustomPlating);
    setCustomSteles(newCustomStele);
    setCustomSoles(newCustomSoles);
  };

  const selectedTexture = () => {
    const typeOfPiece = getIndexType(
      pieceIndex,
      activePieces,
      customSteles,
      customPlatings,
      customSoles,
    );
    const localIndex = getLocalIndex(
      pieceIndex,
      activePieces,
      customSteles,
      customPlatings,
      customSoles,
    );

    if (typeOfPiece === 'activePieces') {
      return activePieces[localIndex]?.granit;
    }
    if (typeOfPiece === 'customStele') {
      return customSteles[localIndex]?.granit;
    }
    if (typeOfPiece === 'customPlating') {
      return customPlatings[localIndex]?.granit;
    }
    if (typeOfPiece === 'customSole') {
      return customSoles[localIndex]?.granit;
    }
  };

  const activeGranits = useMemo(() => {
    const allPieces = [...activePieces, ...customSteles, ...customPlatings, ...customSoles];
    const granitIds = allPieces.map((el) => el.granit?.id).filter((el) => el !== undefined);
    const uniqueIds = [...new Set(granitIds)];

    const granitsObject = uniqueIds?.map((el: string) => {
      const granit = allPieces?.find((element) => element.granit?.id === el)?.granit;

      return {
        id: el,
        granit,
      };
    });

    return granitsObject;
  }, [activePieces, customSteles, customPlatings, customSoles]);

  const selectPieceWithGranitId = (granitId: string) => {
    for (const [elIndex, el] of activePieces.entries()) {
      if (el.granit?.id === granitId) {
        const globalIndex = getGlobalIndex(
          elIndex,
          'activePieces',
          activePieces,
          customSteles,
          customPlatings,
          customSoles,
        );
        setPieceIndex(globalIndex);
        return;
      }
    }
    for (const [elIndex, el] of customSteles.entries()) {
      if (el.granit?.id === granitId) {
        const globalIndex = getGlobalIndex(
          elIndex,
          'customStele',
          activePieces,
          customSteles,
          customPlatings,
          customSoles,
        );
        setPieceIndex(globalIndex);
        return;
      }
    }
    for (const [elIndex, el] of customPlatings.entries()) {
      if (el.granit?.id === granitId) {
        const globalIndex = getGlobalIndex(
          elIndex,
          'customPlating',
          activePieces,
          customSteles,
          customPlatings,
          customSoles,
        );
        setPieceIndex(globalIndex);
        return;
      }
    }
    for (const [elIndex, el] of customSoles.entries()) {
      if (el.granit?.id === granitId) {
        const globalIndex = getGlobalIndex(
          elIndex,
          'customSole',
          activePieces,
          customSteles,
          customPlatings,
          customSoles,
        );
        setPieceIndex(globalIndex);
        return;
      }
    }
  };

  const filteredGranits = useMemo(() => {
    let granitsToFilter = granits;
    if (pieceIndex < activePieces.length) {
      granitsToFilter = granitsToFilter.filter((el) => {
        if (activePieces[pieceIndex]?.underAssembly) {
          return activePieces[pieceIndex].underAssembly?.fabrications?.includes(el.fabrication);
        }
        return activePieces[pieceIndex]?.fabrications?.includes(el.fabrication);
      });
    }
    const filtered = granitsToFilter.filter((el) => {
      if (origin.length > 0 && !origin.includes(el.fabrication)) {
        if (!origin.includes('IMPORT') && el.fabrication !== 'FRENCH') return false;
        if (!origin.includes('FRENCH') && el.fabrication === 'FRENCH') return false;
      }
      return (
        el.name
          ?.toLowerCase()
          .replace(' ', '')
          .includes(filter.toLocaleLowerCase().replace(' ', '')) &&
        categories.every((category) => el.categories.includes(category))
      );
    });

    if (seeAssemblyStock) {
      const assemblyGranits = [...new Set(activePieces.map((el) => el.granit?.id))];

      const selectedGranit = activePieces[pieceIndex]?.granit;
      const notSelectedGranits = assemblyGranits.filter((el) => el !== selectedGranit?.id);

      const notSelectedGranitsPieces = activePieces.filter((el) =>
        notSelectedGranits.includes(el.granit?.id),
      );

      const selectedGranitsPieces = activePieces.filter(
        (el) => el.granit?.id === selectedGranit?.id,
      );

      if (notSelectedGranits.length > 1) {
        return [];
      }

      const availableStocks = assemblyStockData?.filter((el) => {
        const assemblyStockGranits = [
          ...new Set(el.stockPieces?.map((element) => element.granit?.id)),
        ];

        if (assemblyStockGranits.length > 2) {
          return false;
        }

        const stockGranits = [...new Set(el.stockPieces?.map((stockPiece) => stockPiece.granitId))];

        if (stockGranits.every((stockGranit) => assemblyGranits.includes(stockGranit))) {
          return false;
        }

        const hasTheSameNotSelectedGranits = notSelectedGranitsPieces.every(
          (notSelectedGranitsPiece) => {
            const stockPiece = el.stockPieces?.find(
              (p) => p.pieceId === notSelectedGranitsPiece.id,
            );

            if (!stockPiece) {
              return false;
            }

            return stockPiece.granitId === notSelectedGranitsPiece.granit?.id;
          },
        );

        const hasTheSameSelectedGranit = selectedGranitsPieces.every((selectedGranitsPiece) => {
          const stockPiece = el.stockPieces?.find((p) => p.pieceId === selectedGranitsPiece.id);

          if (!stockPiece) {
            return false;
          }

          if (
            notSelectedGranits[0] &&
            selectedGranitsPiece.granit?.id === notSelectedGranits[0].id
          ) {
            return false;
          }
          return true;
        });

        return hasTheSameNotSelectedGranits && hasTheSameSelectedGranit;
      });

      const availableGranits = new Set(
        availableStocks
          .flatMap((el) => el.stockPieces?.map((p) => p.granitId))
          .filter((el) => el !== activePieces[pieceIndex]?.granit?.id),
      );

      return filtered.filter(
        (el) => availableGranits.has(el.id) && el.id !== notSelectedGranits[0],
      );
    }
    return filtered;
  }, [
    pieceIndex,
    activePieces,
    granits,
    filter,
    origin,
    categories,
    assemblyStockData,
    seeAssemblyStock,
  ]);

  const sortedGranits = useMemo(() => {
    return filteredGranits.sort((a, b) => {
      if (sort === 'ascending_name') {
        return a.name.localeCompare(b.name);
      }
      if (sort === 'descending_name') {
        return b.name.localeCompare(a.name);
      }
      if (sort === 'ascending_price') {
        return a.volumePrice - b.volumePrice;
      }
      if (sort === 'descending_price') {
        return b.volumePrice - a.volumePrice;
      }
      return 0;
    });
  }, [sort, filteredGranits]);

  const handleChangeGranit = (granit: Granit) => {
    setError(undefined);
    if (selectedDominant) {
      const piecesIdsToChange: number[] = [];
      for (const [elementIndex, element] of activePieces.entries()) {
        if (element.granit?.id === selectedDominant) {
          if (element.underAssembly) {
            if (element.underAssembly.fabrications.includes(granit.fabrication))
              piecesIdsToChange.push(elementIndex);
          } else if (element.fabrications.includes(granit.fabrication)) {
            piecesIdsToChange.push(elementIndex);
          }
        }
      }
      for (const [elementIndex, element] of customSteles.entries()) {
        if (element.granit?.id === selectedDominant) {
          const globalIndex = getGlobalIndex(
            elementIndex,
            'customStele',
            activePieces,
            customSteles,
            customPlatings,
            customSoles,
          );

          piecesIdsToChange.push(globalIndex);
        }
      }
      for (const [elementIndex, element] of customPlatings.entries()) {
        if (element.granit?.id === selectedDominant) {
          const globalIndex = getGlobalIndex(
            elementIndex,
            'customPlating',
            activePieces,
            customSteles,
            customPlatings,
            customSoles,
          );

          piecesIdsToChange.push(globalIndex);
        }
      }
      for (const [elementIndex, element] of customSoles.entries()) {
        if (element.granit?.id === selectedDominant) {
          const globalIndex = getGlobalIndex(
            elementIndex,
            'customSole',
            activePieces,
            customSteles,
            customPlatings,
            customSoles,
          );

          piecesIdsToChange.push(globalIndex);
        }
      }
      changeTexture(granit, piecesIdsToChange);
      setSelectedDominant(granit.id);
    } else if (hasSelectedAllPieces) {
      const piecesIdsToChange: number[] = [];

      for (const [elementIndex, element] of activePieces.entries()) {
        if (
          element.fabrications?.includes(granit.fabrication) ||
          element.underAssembly?.fabrications?.includes(granit.fabrication)
        ) {
          piecesIdsToChange.push(elementIndex);
        }
      }
      for (const [elementIndex, element] of customSteles.entries()) {
        const globalIndex = getGlobalIndex(
          elementIndex,
          'customStele',
          activePieces,
          customSteles,
          customPlatings,
          customSoles,
        );

        piecesIdsToChange.push(globalIndex);
      }
      for (const [elementIndex, element] of customPlatings.entries()) {
        const globalIndex = getGlobalIndex(
          elementIndex,
          'customPlating',
          activePieces,
          customSteles,
          customPlatings,
          customSoles,
        );
        piecesIdsToChange.push(globalIndex);
      }
      for (const [elementIndex, element] of customSoles.entries()) {
        const globalIndex = getGlobalIndex(
          elementIndex,
          'customSole',
          activePieces,
          customSteles,
          customPlatings,
          customSoles,
        );
        piecesIdsToChange.push(globalIndex);
      }

      changeTexture(granit, piecesIdsToChange);
    } else {
      changeTexture(granit, [pieceIndex]);
    }
  };

  return (
    <Container>
      <AdvancedMode getValue={getValue} setValue={setValue} />
      <ChipContainer>
        <Typography
          variant="body2"
          color="#667085"
          style={{
            fontFamily: 'Open Sans',
            fontStyle: 'normal',
            fontWeight: 600,
            fontSize: '12px',
          }}
        >
          Sélectionner
        </Typography>
        <Chip
          color={hasSelectedAllPieces ? 'primary' : 'default'}
          label="Toutes les pièces"
          onClick={() => {
            if (hasSelectedAllPieces) {
              setHasSelectedAllPieces(false);
              setSelectedDominant(undefined);
              setValue('hasAdvancedMode', false);
            } else {
              setHasSelectedAllPieces(true);
              setSelectedDominant(undefined);
              setValue('hasAdvancedMode', true);
            }
          }}
        />
        {activeGranits.map((el, elIndex) => {
          return (
            <Chip
              avatar={
                <Image
                  src={`${buckets?.BUCKET_3D_PIECES || ''}/${el.granit?.reducedImage}`}
                  alt="texture"
                  borderRadius={50}
                />
              }
              color={selectedDominant === el.id ? 'primary' : 'default'}
              label={`Dominante ${el.granit?.name}`}
              onClick={() => {
                if (selectedDominant === el.id) {
                  setSelectedDominant(undefined);
                  setValue('hasAdvancedMode', false);
                } else {
                  setValue('hasAdvancedMode', true);
                  setSelectedDominant(el.id);
                  selectPieceWithGranitId(el.id);
                  setHasSelectedAllPieces(false);
                }
              }}
            />
          );
        })}
      </ChipContainer>

      {error ? (
        <Typography
          variant="subtitle2"
          sx={{
            fontFamily: 'Open Sans',
            fontStyle: 'normal',
            fontWeight: 600,
            marginBottom: '12px',
            paddingBottom: '16px',
            paddingTop: '16px',
          }}
          color="error"
        >
          {error}
        </Typography>
      ) : (
        <Typography
          variant="body2"
          color="#667085"
          sx={{
            fontFamily: 'Open Sans',
            fontStyle: 'normal',
            fontWeight: 600,
            fontSize: '12px',
            paddingBottom: '16px',
            paddingTop: '16px',
          }}
        >
          Ou sélectionner une pièce sur le monument pour changer le granit.
        </Typography>
      )}
      <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
        <TextField
          id="outlined-basic"
          label="Rechercher un mot clé..."
          variant="outlined"
          sx={{
            width: '75%',
            input: {
              color: '#98A2B3',
              fontFamily: 'Inter',
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '12px',
            },
          }}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon style={{ color: '#98A2B3' }} />,
          }}
        />

        <Select
          style={{ height: '50px', width: '100px' }}
          value={categories}
          multiple
          onChange={(e) => {
            setCategories(e.target.value as string[]);
          }}
        >
          {GranitCategories.map((el) => {
            const label = granitCategoriesLabels.find((l) => l.key === el)?.label ?? el;
            return <MenuItem value={el}>{label}</MenuItem>;
          })}
        </Select>
      </div>

      <Chip
        style={{ marginTop: '10px', padding: '8px' }}
        label={`Voir le stock de monument ${
          activeGranits.length > 1
            ? activeGranits
                .filter((el) => el.id !== selectedTexture()?.id)
                .map((el) => el.granit.name)
                .join('/ ')
            : ''
        }${activeGranits.length > 1 ? '/...' : ''}`}
        onClick={() => {
          if (seeAssemblyStock) {
            setSelectedDominant(undefined);
          } else {
            setSelectedDominant(activePieces[pieceIndex]?.granit?.id);
          }

          setSeeAssemblyStock(!seeAssemblyStock);
        }}
        color={seeAssemblyStock ? 'primary' : 'default'}
      />

      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          marginTop: '10px',
          marginBottom: '4px',
          gap: '8px',
        }}
      >
        <Chip
          avatar={
            sort.includes('name') ? (
              <>{sort === 'descending_name' ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}</>
            ) : (
              <RemoveIcon />
            )
          }
          label="Nom"
          onClick={() => {
            if (sort === 'ascending_name') {
              setSort('descending_name');
            } else {
              setSort('ascending_name');
            }
          }}
        />
        <Chip
          avatar={
            sort.includes('price') ? (
              <>{sort === 'descending_price' ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}</>
            ) : (
              <RemoveIcon />
            )
          }
          label="Prix M3"
          onClick={() => {
            if (sort === 'ascending_price') {
              setSort('descending_price');
            } else {
              setSort('ascending_price');
            }
          }}
        />
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          marginTop: '4px',
          marginBottom: '10px',
          gap: '8px',
        }}
      >
        <Chip
          label="Français"
          color={origin.includes('FRENCH') ? 'primary' : 'default'}
          onClick={() => {
            if (origin.includes('FRENCH')) {
              setOrigin(origin.filter((el) => el !== 'FRENCH'));
            } else {
              setOrigin([...origin, 'FRENCH']);
            }
          }}
        />
        <Chip
          label="Import"
          color={origin.includes('IMPORT') ? 'primary' : 'default'}
          onClick={() => {
            if (origin.includes('IMPORT')) {
              setOrigin(origin.filter((el) => el !== 'IMPORT'));
            } else {
              setOrigin([...origin, 'IMPORT']);
            }
          }}
        />
      </div>

      <GranitContainer>
        {sortedGranits.map((el, index) => {
          let hasStock = false;

          if (type === 'customStele') {
            hasStock = !!stockStelesData?.find((stock) => {
              return isTwoSteleEqual(stock.stele, piece) && stock.granitId === el.id;
            });
          } else {
            hasStock = !!stockData?.find((stock) => stock.granitId === el.id);
          }

          if (seeAssemblyStock) hasStock = true;

          return (
            <ImageContainer
              key={index}
              imageUrl={`${buckets?.BUCKET_3D_PIECES || ''}/${el.reducedImage}`}
              isSelected={selectedTexture()?.name === el.name}
              onClick={() => handleChangeGranit(el)}
            >
              {hasStock ? (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    padding: '6px',
                  }}
                >
                  <Inventory2OutlinedIcon style={{ color: '#fff', fontSize: '20px' }} />
                </div>
              ) : null}
              <ImageNameContainer>
                <Typography
                  variant="body2"
                  color="#344054"
                  style={{
                    fontFamily: 'Open Sans',
                    fontStyle: 'normal',
                    fontWeight: 600,
                    fontSize: '12px',
                    padding: '8px',
                  }}
                >
                  {el.name} {el.eco ? '(Eco granit)' : ''}
                </Typography>
              </ImageNameContainer>
            </ImageContainer>
          );
        })}

        {!sortedGranits || sortedGranits.length === 0 ? (
          <Typography variant="body2" color="#667085">
            Aucun granit disponible
          </Typography>
        ) : null}
      </GranitContainer>
    </Container>
  );
};

const Container = styled('div')({
  padding: '22px',
  display: 'flex',
  flexDirection: 'column',

  maxHeight: window.innerHeight - 294,
  overflowY: 'auto',
});

const GranitContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: '22px',
});

const ChipContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: '12px',
  alignItems: 'center',
});

const Image = styled('img')(
  ({ isSelected, borderRadius }: { isSelected?: boolean; borderRadius?: number }) => ({
    width: '140px',
    height: '140px',
    borderRadius: borderRadius === undefined ? 5 : borderRadius,
    cursor: 'pointer',
    border: `${isSelected ? '4' : '0'}px solid #BC9A67`,
  }),
);

const ImageContainer = styled('div')(
  ({ isSelected, imageUrl }: { imageUrl: string; isSelected?: boolean }) => ({
    display: 'flex',
    alignItems: 'flex-end',
    width: '140px',
    height: '140px',
    borderRadius: 10,
    cursor: 'pointer',
    border: `${isSelected ? '3' : '0'}px solid #BC9A67`,
    backgroundImage: `url("${imageUrl}")`,
    backgroundSize: '140px',
    position: 'relative',
  }),
);

const ImageNameContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#F2F4F7',
  width: '100%',
  borderBottomLeftRadius: 5,
  borderBottomRightRadius: 5,
});

const IOSSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.mode === 'dark' ? '#BC9A67' : '#BC9A67',
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#BC9A67',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[600],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));

const Row = styled('div')({
  display: 'flex',
  flexDirection: 'row',
});
